import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from '@/components/ui/form';
import { PlannerConfigEntityWithOptionalFields } from '@/services/configurator/PlannerConfigTypes';
import { Control } from 'react-hook-form';
import { ReactElement, ReactNode } from 'react';
import { usePillInputLogic } from './hook/usePillInputLogic';
import { PillInputBase } from './PillInputBase';
import { Lock, Unlock } from 'lucide-react';
import { useUser } from '@/components/providers/user-provider';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { normalizedValue } from '@/utils/normalizedValue';

export type InputFields =
  | 'productsInclude'
  | 'productsExclude'
  | 'rangeInclude'
  | 'rangeExclude'
  | 'phraseStrings.keywords';

type FormPillInputComponentProps = {
  formControl: Control<PlannerConfigEntityWithOptionalFields>;
  name: InputFields;
  formLabel: ReactNode;
  formDescription: string;
  disabled: boolean;
  regexpValidation: RegExp | string;
  placeholder?: string;
  badgeSize?: number;
  syncedPhraseKeys?: Set<string>;
  formMessage?: ReactElement;
};

export default function FormPillInputComponent({
  formControl,
  name,
  formLabel,
  formDescription,
  disabled,
  regexpValidation,
  placeholder,
  badgeSize,
  syncedPhraseKeys,
  formMessage,
}: FormPillInputComponentProps) {
  const phraseKey = name.startsWith('phraseStrings.')
    ? name.replace('phraseStrings.', '')
    : null;
  const isKeyLocked = phraseKey
    ? (syncedPhraseKeys?.has(phraseKey) ?? false)
    : false;

  const user = useUser();
  const isAdmin = user.role === 'admin';

  const isDisabled = disabled || (isKeyLocked && !isAdmin);

  const {
    value,
    setValue,
    inputError,
    setInputError,
    submitChange,
    handleDelete,
  } = usePillInputLogic(regexpValidation);
  return (
    <FormField
      control={formControl}
      name={name}
      render={({ field }) => {
        const badges = normalizedValue(field.value);
        return (
          <FormItem>
            <FormLabel className='flex items-center gap-2'>
              {formLabel}
              {isKeyLocked && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      {isAdmin ? (
                        <Unlock className='h-4 w-4 text-muted-foreground' />
                      ) : (
                        <Lock className='h-4 w-4 text-muted-foreground' />
                      )}
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        This field is read-only. Edit directly in Phrase if
                        changes are needed.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </FormLabel>
            <FormMessage>{formMessage}</FormMessage>
            <FormControl>
              <PillInputBase
                badges={badges}
                badgeSize={badgeSize}
                value={value}
                disabled={isDisabled}
                placeholder={placeholder}
                inputError={inputError}
                onInputChange={(val) => {
                  setValue(val);
                  if (!val) {
                    setInputError('');
                  }
                }}
                onDelete={(item) => handleDelete(item, field, badges)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    submitChange(value, field, badges);
                  }
                }}
                onBlur={(e) => {
                  const root = e.currentTarget.closest(
                    '[data-pill-input-root]',
                  );
                  if (root && root.contains(e.relatedTarget)) {
                    return;
                  }
                  if (value.trim()) {
                    submitChange(value, field, badges);
                  }
                }}
              />
            </FormControl>
            <FormDescription>{formDescription}</FormDescription>
          </FormItem>
        );
      }}
    />
  );
}
